// 🚀 ULTRA-LEGENDARY PWA INSTALLER - SHWET ARTISTRY
// Advanced Progressive Web App Installation & Management System
// Provides seamless app-like experience with intelligent install prompts

class PWAInstaller {
  constructor() {
    this.deferredPrompt = null;
    this.isInstalled = false;
    this.installSource = null;
    this.init();
  }

  init() {
    this.detectInstallation();
    this.setupEventListeners();
    this.trackInstallMetrics();
    this.showInstallBanner();
  }

  // 📱 Detect if app is already installed
  detectInstallation() {
    // Check if running as PWA
    this.isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                     window.navigator.standalone ||
                     document.referrer.includes('android-app://');

    if (this.isInstalled) {
      this.handleInstalledApp();
    }
  }

  // 🎯 Setup installation event listeners
  setupEventListeners() {
    // Capture install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('🎯 PWA install prompt available');
      e.preventDefault();
      this.deferredPrompt = e;
      this.showInstallButton();
    });

    // Track successful installation
    window.addEventListener('appinstalled', (e) => {
      console.log('🎉 PWA installed successfully');
      this.handleSuccessfulInstall();
    });

    // Handle install button clicks
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-pwa-install]')) {
        this.promptInstall();
      }
    });
  }

  // 💫 Show smart install banner
  showInstallBanner() {
    if (this.isInstalled || !this.deferredPrompt) return;

    // Check if user has dismissed banner recently
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    const lastDismissed = dismissed ? new Date(dismissed) : null;
    const daysSinceDismissal = lastDismissed ? 
      (Date.now() - lastDismissed.getTime()) / (1000 * 60 * 60 * 24) : 999;

    if (daysSinceDismissal < 7) return; // Don't show for 7 days

    setTimeout(() => {
      this.createInstallBanner();
    }, 3000); // Show after 3 seconds
  }

  // 🎨 Create beautiful install banner
  createInstallBanner() {
    const banner = document.createElement('div');
    banner.className = 'pwa-install-banner';
    banner.innerHTML = `
      <div class="pwa-banner-content">
        <div class="pwa-banner-icon">🎨</div>
        <div class="pwa-banner-text">
          <h3>Install Shwet Artistry App</h3>
          <p>Get faster access with offline browsing</p>
        </div>
        <div class="pwa-banner-actions">
          <button class="pwa-install-btn" data-pwa-install>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L12 14M12 14L8 10M12 14L16 10M5 17L5 19C5 20.1046 5.89543 21 7 21L17 21C18.1046 21 19 20.1046 19 19L19 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Install
          </button>
          <button class="pwa-dismiss-btn" onclick="this.closest('.pwa-install-banner').remove(); localStorage.setItem('pwa-banner-dismissed', new Date().toISOString())">
            ✕
          </button>
        </div>
      </div>
    `;

    // Add styles
    const styles = `
      .pwa-install-banner {
        position: fixed;
        bottom: 20px;
        left: 20px;
        right: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border-radius: 12px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        z-index: 10000;
        animation: slideUp 0.5s ease-out;
        max-width: 400px;
        margin: 0 auto;
      }
      
      .pwa-banner-content {
        display: flex;
        align-items: center;
        padding: 16px;
        gap: 12px;
      }
      
      .pwa-banner-icon {
        font-size: 2rem;
        flex-shrink: 0;
      }
      
      .pwa-banner-text h3 {
        margin: 0 0 4px 0;
        font-size: 1.1rem;
        font-weight: 600;
      }
      
      .pwa-banner-text p {
        margin: 0;
        font-size: 0.9rem;
        opacity: 0.9;
      }
      
      .pwa-banner-actions {
        display: flex;
        gap: 8px;
        margin-left: auto;
      }
      
      .pwa-install-btn {
        background: rgba(255,255,255,0.2);
        border: 1px solid rgba(255,255,255,0.3);
        color: white;
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 0.9rem;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s ease;
      }
      
      .pwa-install-btn:hover {
        background: rgba(255,255,255,0.3);
        transform: translateY(-1px);
      }
      
      .pwa-dismiss-btn {
        background: none;
        border: none;
        color: white;
        font-size: 1.2rem;
        cursor: pointer;
        opacity: 0.7;
        padding: 4px;
        border-radius: 4px;
        transition: opacity 0.2s ease;
      }
      
      .pwa-dismiss-btn:hover {
        opacity: 1;
      }
      
      @keyframes slideUp {
        from { transform: translateY(100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      
      @media (max-width: 768px) {
        .pwa-install-banner {
          left: 10px;
          right: 10px;
          bottom: 10px;
        }
      }
    `;

    // Inject styles
    if (!document.querySelector('#pwa-banner-styles')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'pwa-banner-styles';
      styleSheet.textContent = styles;
      document.head.appendChild(styleSheet);
    }

    document.body.appendChild(banner);

    // Auto-hide after 10 seconds
    setTimeout(() => {
      if (banner.parentNode) {
        banner.style.animation = 'slideUp 0.3s ease-in reverse';
        setTimeout(() => banner.remove(), 300);
      }
    }, 10000);
  }

  // 🚀 Prompt user to install
  async promptInstall() {
    if (!this.deferredPrompt) {
      this.showFallbackInstructions();
      return;
    }

    try {
      const result = await this.deferredPrompt.prompt();
      console.log('📱 Install prompt result:', result.outcome);
      
      if (result.outcome === 'accepted') {
        this.installSource = 'prompt';
        this.trackInstallation('prompt-accepted');
      } else {
        this.trackInstallation('prompt-dismissed');
      }

      this.deferredPrompt = null;
      this.removeInstallButton();
      
    } catch (error) {
      console.error('❌ Install prompt failed:', error);
      this.showFallbackInstructions();
    }
  }

  // 📱 Show manual install instructions
  showFallbackInstructions() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    let instructions = '';
    if (isIOS) {
      instructions = `
        <div class="install-instructions">
          <h3>📱 Install on iPhone/iPad</h3>
          <ol>
            <li>Tap the <strong>Share</strong> button <span style="font-size: 1.2em;">⬆️</span></li>
            <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
            <li>Tap <strong>"Add"</strong> to install</li>
          </ol>
        </div>
      `;
    } else if (isAndroid) {
      instructions = `
        <div class="install-instructions">
          <h3>📱 Install on Android</h3>
          <ol>
            <li>Tap the <strong>menu</strong> (⋮) in your browser</li>
            <li>Select <strong>"Add to Home screen"</strong></li>
            <li>Tap <strong>"Add"</strong> to install</li>
          </ol>
        </div>
      `;
    } else {
      instructions = `
        <div class="install-instructions">
          <h3>💻 Install on Desktop</h3>
          <ol>
            <li>Look for the <strong>install icon</strong> in your address bar</li>
            <li>Or check browser menu for <strong>"Install App"</strong></li>
            <li>Click to install for faster access</li>
          </ol>
        </div>
      `;
    }

    this.showModal(instructions);
  }

  // 🎉 Handle successful installation
  handleSuccessfulInstall() {
    this.isInstalled = true;
    this.removeInstallButton();
    this.trackInstallation('installed');
    
    // Show success message
    this.showNotification('🎉 App installed successfully!', 'You can now access Shwet Artistry from your home screen');
    
    // Remove install banner
    const banner = document.querySelector('.pwa-install-banner');
    if (banner) banner.remove();
  }

  // 📊 Track installation metrics
  trackInstallation(event) {
    try {
      // Analytics tracking
      if (typeof gtag !== 'undefined') {
        gtag('event', 'pwa_install', {
          event_category: 'engagement',
          event_label: event,
          install_source: this.installSource || 'unknown'
        });
      }

      // Custom analytics
      fetch('/api/analytics/pwa-install', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event,
          source: this.installSource,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {}); // Silent fail

    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  }

  // 🔧 Utility methods
  showInstallButton() {
    let button = document.querySelector('.pwa-install-button');
    if (!button) {
      button = document.createElement('button');
      button.className = 'pwa-install-button';
      button.innerHTML = '📱 Install App';
      button.dataset.pwaInstall = '';
      
      // Find suitable location (header, footer, etc.)
      const header = document.querySelector('header') || document.querySelector('.header');
      if (header) {
        header.appendChild(button);
      }
    }
  }

  removeInstallButton() {
    const button = document.querySelector('.pwa-install-button');
    if (button) button.remove();
  }

  showModal(content) {
    const modal = document.createElement('div');
    modal.className = 'pwa-modal';
    modal.innerHTML = `
      <div class="pwa-modal-overlay" onclick="this.closest('.pwa-modal').remove()"></div>
      <div class="pwa-modal-content">
        ${content}
        <button onclick="this.closest('.pwa-modal').remove()" style="margin-top: 20px; padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 6px; cursor: pointer;">Got it!</button>
      </div>
    `;

    const modalStyles = `
      .pwa-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .pwa-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
      }
      .pwa-modal-content {
        background: white;
        padding: 30px;
        border-radius: 12px;
        max-width: 400px;
        margin: 20px;
        position: relative;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      }
      .install-instructions ol {
        padding-left: 20px;
      }
      .install-instructions li {
        margin-bottom: 8px;
        line-height: 1.5;
      }
    `;

    if (!document.querySelector('#pwa-modal-styles')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'pwa-modal-styles';
      styleSheet.textContent = modalStyles;
      document.head.appendChild(styleSheet);
    }

    document.body.appendChild(modal);
  }

  showNotification(title, body) {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/assets/icon-192.png' });
    }
  }

  handleInstalledApp() {
    console.log('🎨 Running as installed PWA');
    
    // Add PWA-specific styles
    document.documentElement.classList.add('pwa-installed');
    
    // Track PWA usage
    this.trackInstallation('pwa-opened');
    
    // Enhanced PWA features
    this.enablePWAFeatures();
  }

  enablePWAFeatures() {
    // Add to home screen promotion is not needed
    const installElements = document.querySelectorAll('[data-pwa-install]');
    installElements.forEach(el => el.style.display = 'none');
    
    // Enable advanced PWA features
    this.setupShortcuts();
    this.enableOfflineIndicator();
  }

  setupShortcuts() {
    // Add keyboard shortcuts for PWA
    document.addEventListener('keydown', (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch(e.key) {
          case '1':
            e.preventDefault();
            window.location.href = '/';
            break;
          case '2':
            e.preventDefault();
            window.location.href = '/collections/all';
            break;
          case '3':
            e.preventDefault();
            window.location.href = '/cart';
            break;
        }
      }
    });
  }

  enableOfflineIndicator() {
    // Show offline status
    const indicator = document.createElement('div');
    indicator.className = 'offline-indicator';
    indicator.textContent = '📶 Online';
    
    const updateStatus = () => {
      indicator.textContent = navigator.onLine ? '📶 Online' : '📴 Offline';
      indicator.className = `offline-indicator ${navigator.onLine ? 'online' : 'offline'}`;
    };

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    
    updateStatus();
  }

  trackInstallMetrics() {
    // Track PWA performance metrics
    if ('performance' in window) {
      window.addEventListener('load', () => {
        const perfData = performance.getEntriesByType('navigation')[0];
        if (perfData) {
          console.log('📊 PWA Load Time:', perfData.loadEventEnd - perfData.fetchStart, 'ms');
        }
      });
    }
  }
}

// 🚀 Initialize PWA Installer
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PWAInstaller());
} else {
  new PWAInstaller();
}

console.log('🎨 Shwet Artistry PWA Installer loaded!');